/**
 * CoachingLogTab — all coaching sessions for the authenticated coach.
 *
 * Columns: Date/Time, Client, Session name, Duration, Type, Status, Barter note.
 * Filters: date range, client name text filter, appointment_type multiselect, status multiselect.
 * Sorting: click any column header to sort asc/desc (client-side on loaded page).
 * Infinite scroll via "Load more" button (offset/limit pagination).
 * Export: CSV download, PDF via browser print.
 *
 * Data notes:
 *   - scheduled_at is an ISO 8601 string (UTC); displayed in the browser's local timezone.
 *   - appointment_type: 'billable' | 'pro_bono' | 'barter'
 *   - status: 'scheduled' | 'completed' | 'cancelled'
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import { APPOINTMENT_TYPES, APPOINTMENT_TYPE_DARK_BADGES, getAppointmentTypeLabel } from '../constants/appointmentTypes';

// ── Types ──────────────────────────────────────────────────────────────────────

interface LogSession {
  appointment_id: string;
  meeting_name: string;
  client_name: string;
  /** ISO 8601 UTC string, e.g. "2026-05-15T14:00:00+00:00" */
  scheduled_at: string;
  duration_minutes: number;
  /** 'billable' | 'pro_bono' | 'barter' */
  appointment_type: string;
  /** 'scheduled' | 'completed' | 'cancelled' */
  status: string;
  barter_note: string | null;
}

const PAGE_SIZE = 50;

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  /* Input: ISO 8601 UTC string from the API.
   * Output: date and time in the user's local timezone, e.g. "May 15, 2026, 2:00 PM". */
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function typeLabel(t: string): { label: string; cls: string } {
  return APPOINTMENT_TYPE_DARK_BADGES[t as keyof typeof APPOINTMENT_TYPE_DARK_BADGES]
    ?? { label: t, cls: 'bg-gray-800 text-gray-400 border-gray-700' };
}

function statusLabel(s: string): { label: string; cls: string } {
  switch (s) {
    case 'completed':  return { label: 'Completed',  cls: 'bg-green-900/40 text-green-400 border-green-800' };
    case 'scheduled':  return { label: 'Scheduled',  cls: 'bg-yellow-900/40 text-yellow-400 border-yellow-800' };
    case 'cancelled':  return { label: 'Cancelled',  cls: 'bg-gray-800 text-gray-400 border-gray-700' };
    default:           return { label: s,             cls: 'bg-gray-800 text-gray-400 border-gray-700' };
  }
}

// ── Multiselect chip component ─────────────────────────────────────────────────

interface MultiSelectProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  };

  const displayLabel = selected.length === 0
    ? label
    : selected.length === options.length
      ? `${label}: All`
      : selected.map(v => options.find(o => o.value === v)?.label ?? v).join(', ');

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        className="flex items-center gap-1.5 bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 hover:border-gray-600 focus:outline-none focus:ring-1 focus:ring-primary-500 whitespace-nowrap"
      >
        {displayLabel}
        <svg className="w-3 h-3 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-20 bg-dark-card border border-dark-border rounded-lg shadow-xl py-1 min-w-[150px]">
          {options.map(o => (
            <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-dark-hover cursor-pointer">
              <input
                type="checkbox"
                className="accent-primary-500"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── CSV / PDF export ───────────────────────────────────────────────────────────

function exportCsv(sessions: LogSession[]): void {
  const headers = ['Date/Time', 'Client', 'Session', 'Duration (min)', 'Type', 'Status', 'Barter Note'];
  const rows = sessions.map(s => [
    s.scheduled_at ? formatDateTime(s.scheduled_at) : '',
    s.client_name,
    s.meeting_name,
    s.duration_minutes,
    s.appointment_type,
    s.status,
    s.barter_note ?? '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `coaching_log_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportPdf(sessions: LogSession[]): void {
  /* Opens the browser's print dialog with a print-only table rendered in a new window. */
  const rows = sessions.map(s => `
    <tr>
      <td>${s.scheduled_at ? formatDate(s.scheduled_at) : ''}</td>
      <td>${s.client_name}</td>
      <td>${s.meeting_name}</td>
      <td>${s.duration_minutes}</td>
      <td>${s.appointment_type}</td>
      <td>${s.status}</td>
      <td>${s.barter_note ?? ''}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Coaching Log</title>
  <style>
    body { font-family: sans-serif; font-size: 11px; padding: 20px; }
    h1 { font-size: 16px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; }
    th { background: #f5f5f5; font-weight: 600; }
    tr:nth-child(even) { background: #fafafa; }
  </style>
</head>
<body>
  <h1>Coaching Log — ${new Date().toLocaleDateString()}</h1>
  <table>
    <thead>
      <tr>
        <th>Date</th><th>Client</th><th>Session</th><th>Duration (min)</th>
        <th>Type</th><th>Status</th><th>Barter Note</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}

// ── Component ──────────────────────────────────────────────────────────────────

const TYPE_OPTIONS = APPOINTMENT_TYPES.map(t => ({ value: t, label: getAppointmentTypeLabel(t) }));

const STATUS_OPTIONS = [
  { value: 'scheduled',  label: 'Scheduled' },
  { value: 'completed',  label: 'Completed' },
  { value: 'cancelled',  label: 'Cancelled' },
];

const CoachingLogTab: React.FC = () => {
  const [sessions, setSessions]   = useState<LogSession[]>([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]         = useState('');

  // Filters
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  // Sorting (client-side on the loaded page)
  const [sortCol, setSortCol] = useState<keyof LogSession | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const buildParams = useCallback((offset: number): URLSearchParams => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo)   p.append('date_to', dateTo);
    typeFilter.forEach(t => p.append('appointment_type', t));
    statusFilter.forEach(s => p.append('status', s));
    return p;
  }, [dateFrom, dateTo, typeFilter, statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.sessionsLog}?${buildParams(0)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      setSessions(data.sessions ?? []);
      setTotal(data.total ?? 0);
    } catch {
      setError('Failed to load coaching log.');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.sessionsLog}?${buildParams(sessions.length)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      setSessions(prev => [...prev, ...(data.sessions ?? [])]);
    } catch {
      setError('Failed to load more sessions.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const hasMore = sessions.length < total;

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setClientFilter('');
    setTypeFilter([]);
    setStatusFilter([]);
  };

  const filtersActive = dateFrom || dateTo || clientFilter || typeFilter.length > 0 || statusFilter.length > 0;

  // Client-side: filter by client name, then sort
  const handleSortClick = (col: keyof LogSession) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const displayedSessions = (() => {
    let result = sessions;
    if (clientFilter.trim()) {
      const q = clientFilter.trim().toLowerCase();
      result = result.filter(s => s.client_name.toLowerCase().includes(q));
    }
    if (sortCol) {
      result = [...result].sort((a, b) => {
        const av = a[sortCol] ?? '';
        const bv = b[sortCol] ?? '';
        let cmp = 0;
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv;
        } else {
          cmp = String(av).localeCompare(String(bv));
        }
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  })();

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Coaching Log</h1>
          <p className="text-sm text-gray-500 mt-0.5">All coaching sessions — {total} total</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCsv(sessions)}
            disabled={sessions.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-300 border border-dark-border rounded-lg hover:bg-dark-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            CSV
          </button>
          <button
            onClick={() => exportPdf(sessions)}
            disabled={sessions.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-300 border border-dark-border rounded-lg hover:bg-dark-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 [color-scheme:dark]"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter by client…"
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            className="bg-dark-sidebar border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-primary-500 w-44"
          />
        </div>
        <MultiSelect
          label="Type"
          options={TYPE_OPTIONS}
          selected={typeFilter}
          onChange={setTypeFilter}
        />
        <MultiSelect
          label="Status"
          options={STATUS_OPTIONS}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
        {filtersActive && (
          <button
            onClick={clearFilters}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      {error && <p className="text-sm text-red-400">{error}</p>}

      {loading ? (
        <div className="text-sm text-gray-500">Loading...</div>
      ) : displayedSessions.length === 0 ? (
        <div className="text-sm text-gray-500">No sessions found for the selected filters.</div>
      ) : (
        <>
          <div className="bg-dark-sidebar border border-dark-border rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                  {(
                    [
                      { col: 'scheduled_at',      label: 'Date / Time', align: 'left'  },
                      { col: 'client_name',        label: 'Client',      align: 'left'  },
                      { col: 'meeting_name',       label: 'Session',     align: 'left'  },
                      { col: 'duration_minutes',   label: 'Duration',    align: 'right' },
                      { col: 'appointment_type',   label: 'Type',        align: 'left'  },
                      { col: 'status',             label: 'Status',      align: 'left'  },
                    ] as { col: keyof LogSession; label: string; align: 'left' | 'right' }[]
                  ).map(({ col, label, align }) => (
                    <th
                      key={col}
                      onClick={() => handleSortClick(col)}
                      className={`px-4 py-3 font-medium cursor-pointer select-none hover:text-gray-300 transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        <span className="text-gray-600">
                          {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left font-medium">Barter Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {displayedSessions.map(s => {
                  const tl = typeLabel(s.appointment_type);
                  const sl = statusLabel(s.status);
                  return (
                    <tr key={s.appointment_id} className="hover:bg-dark-hover transition-colors">
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                        {s.scheduled_at ? formatDateTime(s.scheduled_at) : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-300 font-medium whitespace-nowrap">
                        {s.client_name}
                      </td>
                      <td className="px-4 py-3 text-gray-400 max-w-[200px] truncate">
                        {s.meeting_name}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-400 whitespace-nowrap">
                        {s.duration_minutes} min
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${tl.cls}`}>
                          {tl.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-medium ${sl.cls}`}>
                          {sl.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[180px] truncate">
                        {s.barter_note ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Infinite scroll — load more */}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-4 py-2 text-xs font-medium text-gray-300 border border-dark-border rounded-lg hover:bg-dark-hover disabled:opacity-40 transition-colors"
              >
                {loadingMore ? 'Loading…' : `Load more (${sessions.length} of ${total})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CoachingLogTab;
